import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getCurrentUser } from '../../api/common';
import { getDocById, replaceApprovalLines, updateDoc, addDocOpinion, deleteDocFile, addDocFile, addDocAuth, deleteDocAuth, notifyDocSubmit } from '../../api/aprvDoc';
import { formatDate, lineTypeMap } from '../../utils/commonUtils';
import SubFormRouter from './subforms/SubFormRouter';
import AprvLineSelect from '../../components/OrgSelect/AprvLineSelect';
import '../../css/AprvDocForm.css';
import FileAttachment from '../../components/FileAttachment/FileAttachment';


function AprvDocEdit({ onOpenOpinionModal }) {
  const { aprvId } = useParams(); // URL에서 aprvId 추출
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page") || 1;
  const search = queryParams.get("search") || "";
  const status = queryParams.get("status") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  //const [currentUser, setCurrentUser] = useState(null);  
  const [writer, setWriter] = useState(null);

  const [docData, setDocData] = useState(null);
  const [prvApprovalLines, setPrvApprovalLines] = useState([]);
  const [approvalLines, setApprovalLines] = useState([]);
  const [approvalReaders, setApprovalReaders] = useState([]); // 열람자
  const [showLinePopup, setShowLinePopup] = useState(false);
  const [files, setFiles] = useState([]);
  const [docNo, setDocNo] = useState('');
  const [publicType, setPublicType] = useState('user');
  const [docSave, setDocSave] = useState('1');
  const [title, setTitle] = useState('');

  const [formData, setFormData] = useState({});
  const { editorContent, ...jsonFields } = formData;
  const [draftDate, setDraftDate] = useState(formatDate(new Date()));


  const fetchDoc = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const userInfo = await getCurrentUser(token);
      setWriter({
        user_id: userInfo.userid,
        user_name: userInfo.name,
        user_position: userInfo.position,
        user_dept_code: userInfo.dept_code,
        user_dept_name: userInfo.dept_name,
      });
      const data = await getDocById(aprvId, token);
      setDocData(data);
      console.log(data);
      if (data?.lines) {
        setApprovalLines(data.lines);
        setPrvApprovalLines(data.lines);
      }
      if (data?.auths) {
        const readerLines = data.auths.map((auth, idx) => ({
          user_id: auth.target_type === "user" ? auth.target_id : null,
          user_name: auth.target_name,
          user_position: "",
          user_dept_code: auth.target_type === "dept" ? auth.target_id : null,
          user_dept_name: auth.target_type === "dept" ? auth.target_name : "",
          line_type: "lr",
          line_no: 0,
          current_yn: "n",
          line_status: "열람",
          rep_yn: "n",
          end_user_yn: "n"
        }));

        setApprovalReaders(readerLines);
      }
      setDocNo(data.doc.doc_no);
      setPublicType(data.doc.public_type);
      setDocSave(data.doc.doc_save);
      setTitle(data.doc.title);
      setDraftDate(data.doc.make_date);
      setFormData(data.subForms);
      

      if (Array.isArray(data.subForms) && data.subForms.length > 0) {
        const sf = data.subForms[0]; // 첫 번째 보조양식
        let parsedJson = {};

        try {
          // json_data가 문자열 안에 JSON이 또 들어있으므로 두 번 파싱
          parsedJson = JSON.parse(JSON.parse(sf.json_data));
        } catch (e) {
          console.error("subForm json_data 파싱 실패:", e);
        }

        // formData에 html_data와 json_data 병합
        setFormData({
          editorContent: sf.html_data || "",
          ...(parsedJson[sf.sub_form_code] || {})
        });
      }


      if (Array.isArray(data.files)) {
        const existingFiles = data.files.map(f => ({
          id: f.id,
          name: f.originalName,
          size: f.size,
          type: f.mimeType,
          isNew: false,
          isDeleted: false,
        }));
        setFiles(existingFiles);
      }
    } catch (err) {
      setError('문서를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [aprvId]); 

  useEffect(() => {
    fetchDoc();
  }, [fetchDoc]);

  const handleConfirmLineSelection = (selectedTargets) => {
    const approvers = selectedTargets.filter(line => line.line_type !== "lr");
    setApprovalLines(approvers);

    const readers = selectedTargets.filter(line => line.line_type === "lr");
    setApprovalReaders(readers);
    setShowLinePopup(false);
  };

  const handleUpdate = async (action, opinion) => {
    const token = sessionStorage.getItem('token');
 
    // 1) 문서 메타데이터 payload (JSON)
    const payload = {
      doc_no: docNo,
      inout_type: "1",
      sub_form_code: doc.sub_form_code,
      req_date: new Date().toISOString().slice(0, 10), // 오늘 날짜
      doc_save: docSave,
      title,
      attach_yn: files.some(f => !f.isDeleted) ? "y" : "n", // 삭제되지 않은 파일만 체크
      doc_type: "A",
      public_type: publicType,
      doc_status: action === "draft" ? "draft" : "request",
      html_data: editorContent || "",
      json_data: JSON.stringify({
        [doc.sub_form_code]: jsonFields,
      }),
    };

    try {
      // 1) 문서/서브폼 수정
      await updateDoc(aprvId, payload, token);

      // 2) 첨부파일 처리 (플래그 기반)
      if (Array.isArray(files)) {
        // 신규 파일 업로드
        const newFiles = files.filter(f => f.isNew && !f.isDeleted && f.file);
        for (const f of newFiles) {
          await addDocFile(aprvId, f.file, token, doc.form_code);
        }

        // 삭제된 파일 제거
        const deletedFiles = files.filter(f => f.isDeleted && !f.isNew && f.id);
        for (const f of deletedFiles) {
          await deleteDocFile(aprvId, f.id, token);
        }
      }

      // 3) 결재선 수정
      if (Array.isArray(approvalLines) && approvalLines.length > 0) {
        await replaceApprovalLines(aprvId, prvApprovalLines, approvalLines, token);
      }

      // 기존 열람자가 존재 하면  삭제
      if (Array.isArray(docData.auths)) {  
        for (const a of docData.auths) {
          await deleteDocAuth(aprvId, a.target_type, a.target_id, token);
        }
      }
       // 새 열람자 등록
      if (Array.isArray(approvalReaders) && approvalReaders.length > 0) {
        const readerPayload = approvalReaders.map(r => {
          const isDept = !r.user_id && r.user_dept_code;
          return {
            aprv_id: aprvId,
            target_type: isDept ? "dept" : "user",
            target_id: isDept ? r.user_dept_code : r.user_id,
            target_name: isDept ? r.user_dept_name : r.user_name,
            target_from: "doc",
            target_auth: "read",
          };
        });
        await addDocAuth(aprvId, readerPayload, token);
      }


      // 4) 결재 의견 저장 (draft 제외)
      if (action !== "draft" && opinion.trim()) {
        await addDocOpinion(
          aprvId,
          {
            opinion,
            user_id: writer?.user_id,
            user_name: writer?.user_name,
            user_position: writer?.user_position,
            user_dept_code: writer?.user_dept_code,
            user_dept_name: writer?.user_dept_name,
          },
          token
        );
      }

      // 5) 결재 상신 시 첫 번째 결재자에게 알림
      if (action !== "draft") {
        await notifyDocSubmit(aprvId, token, "request");
      }

      alert(action === "draft" ? "임시저장 되었습니다." : "결재 상신이 완료되었습니다.");
      if (action === "draft") {
        navigate("/aprvdoc?status=draft");
      } else {
        navigate("/aprvdoc?status=request");
      }
    } catch (err) {
      console.error("결재 수정 실패:", err);
      alert("결재 수정 중 오류가 발생했습니다.");
    }
  };


  if (loading) return <div className="p-4">⏳ 문서 불러오는 중...</div>;
  if (error) return <div className="p-4 text-danger">❌ {error}</div>;
  if (!docData) return <div className="p-4">문서 데이터가 없습니다.</div>;

  const { doc } = docData;

  return (
    <div className="container mt-4">
      <div className="card shadow">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">📑 {doc?.form_name}</h5>
        </div>
        <div className="card-body">
          <form >

            {/* ✅ 결재선 지정 영역 */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <label className="form-label mb-0">결재선 지정</label>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowLinePopup(true)}
                >
                  결재선 추가
                </button>
              </div>

              <div className="table-responsive mt-2 d-flex justify-content-end">
                <table
                  className="table table-bordered text-center align-middle"
                  style={{ width: `${(approvalLines.length + 1) * 10}%` }}
                >
                  <tbody>
                    <tr className="approval-line-header">
                      <td>기안</td>
                      {approvalLines.map(line => (
                        <td key={line.user_id}>
                          {lineTypeMap[line.line_type]}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>{doc.user_name} / {doc.user_position}</td>
                      {approvalLines.map(line => (
                        <td key={line.user_id}>
                          {line.user_name} / {line.user_position}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>상신</td>
                      {approvalLines.map(line => (
                        <td key={line.user_id}></td>
                      ))}
                    </tr>
                    <tr>
                      <td>{formatDate(draftDate)}</td>
                      {approvalLines.map(line => (
                        <td key={line.user_id}></td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

            </div>

            {/* ✅ 문서 기본 정보 */}
            <table className="table table-bordered">
              <tbody>
                {approvalReaders && approvalReaders.length > 0 && (
                  <tr className="table-four-cols">
                    <td>열람</td>
                    <td colSpan="3">
                      {approvalReaders.map(r => r.user_name).join(", ")}
                    </td>
                  </tr>
                )}

                <tr className="table-four-cols">
                  <td>기안자</td>
                  <td>{doc.user_name} / {doc.user_position}</td>
                  <td>기안부서</td>
                  <td>{doc.user_dept_name}</td>
                </tr>
                <tr className="table-four-cols">
                  <td>문서 번호</td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={docNo}
                      onChange={(e) => setDocNo(e.target.value)}
                    />
                  </td>
                  <td>공개 범위</td>
                  <td>
                    <select
                      className="form-select"
                      value={publicType}
                      onChange={(e) => setPublicType(e.target.value)}
                    >
                      <option value="user">개인</option>
                      <option value="dept">부서</option>
                      <option value="all">전체</option>
                    </select>
                  </td>
                </tr>
                <tr className="table-four-cols">
                  <td>기안일</td>
                  <td>
                    <input
                      type="date"
                      className="form-control"
                      value={formatDate(draftDate)}
                      onChange={(e) => setDraftDate(e.target.value)}
                    />
                  </td>
                  <td>보존연한</td>
                  <td>
                    <select
                      className="form-select"
                      value={docSave}
                      onChange={(e) => setDocSave(e.target.value)}
                    >
                      <option value="">선택</option>
                      <option value="1">1년</option>
                      <option value="3">3년</option>
                      <option value="5">5년</option>
                      <option value="z">영구</option>
                    </select>
                  </td>
                </tr>
                <tr className="table-title">
                  <td>제목</td>
                  <td colSpan="3">
                    <input
                      type="text"
                      className="form-control"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            {/* ✅ 서브폼 */}
            <SubFormRouter
              subFormCode={doc.sub_form_code}
              formData={formData}
              setFormData={setFormData}
              mode="edit"
            />
       
            {/* 첨부파일 */}
            <div className="mb-3">
              <label className="form-label fw-bold">첨부파일</label>
              <FileAttachment files={files} setFiles={setFiles} mode="edit" />
            </div>

            {/* ✅ 액션 버튼 */}
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-secondary me-2"
                onClick={() => handleUpdate("draft", "")}
              >
                임시저장
              </button>

              <button
                type="button"
                className="btn btn-primary me-2"
                onClick={() => onOpenOpinionModal("submit", handleUpdate)}
              >
                결재 상신
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() =>
                  navigate(`/aprvdoc?page=${page}&search=${search}${status ? `&status=${status}` : ''}`)
                }
              >
                취소
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ✅ 결재선 선택 팝업 */}
      {showLinePopup && (
        <AprvLineSelect
          writer={writer}
          onConfirm={handleConfirmLineSelection}
          onCancel={() => setShowLinePopup(false)}
          docStatus={doc?.doc_status}
          initialSelected={[...approvalLines, ...approvalReaders]}
        />
      )}

    </div>
  );

} export default AprvDocEdit;